import { useEffect, useState } from 'react';
import { useSessionContext } from 'supertokens-auth-react/recipe/session';
import { getProductsByUser, getTagsForFilter } from '@/Api';
import { extractProductTags } from '@/utils/tags';
import { useStore } from '@/store/Store';

function uniqueTagsSorted(tags: string[]): string[] {
  return [...new Set(tags)].sort((a, b) => a.localeCompare(b));
}

async function loadUserTagsFromProducts(userId: string): Promise<string[]> {
  const products = await getProductsByUser(userId);
  const tagSet = new Set<string>();
  products.forEach((product) => {
    extractProductTags(product).forEach((tag) => tagSet.add(tag));
  });
  return [...tagSet].sort((a, b) => a.localeCompare(b));
}

/**
 * Platform tag suggestions plus tags the current user has used on their own products.
 * Waits for SuperTokens session before fetching so userTags are scoped correctly.
 */
export function useProductTagSuggestions() {
  const session = useSessionContext();
  const userId = useStore((state) => state.userProfile._id);
  const [suggestedTags, setSuggestedTags] = useState<string[]>([]);
  const [previousTags, setPreviousTags] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (session.loading) return;

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const response = await getTagsForFilter();
        if (cancelled) return;

        const platform = uniqueTagsSorted(response.suggested ?? []);
        let userTags = uniqueTagsSorted(response.userTags ?? []);

        if (userTags.length === 0 && userId) {
          userTags = await loadUserTagsFromProducts(userId);
          if (cancelled) return;
        }

        setSuggestedTags(platform);
        setPreviousTags(userTags);
      } catch {
        if (!cancelled) {
          setSuggestedTags([]);
          setPreviousTags([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [session.loading, userId]);

  return { suggestedTags, previousTags, loading };
}
